import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { LeagueProjectionAttributes } from '@library/types/leagues';
import { ProjectionRow } from '@bo-components/simulations/steps/StepThreeFinalize/ProjectionRow';
import { ProjectionsHeader } from '@bo-components/simulations/steps/StepThreeFinalize/ProjectionsHeader';
import { StatTypeHeader } from '@bo-components/simulations/steps/StepThreeFinalize/StatTypeHeader';
import { classNames } from '@library/utils';
import { findProjectionByProjectionId } from '@bo-utils/projection';
import { useState } from 'react';

export interface ProjectionRowsProps {
  marketExistingProjections: Array<GamePlayerProjectionTrading>;
  newAndChangedProjections: Array<GamePlayerProjectionTrading>;
  onBulkTimeChange: ({
    projectionBoardTime,
    projectionStartTime,
    simsProjections,
    updateProjection
  }: {
    simsProjections: Array<GamePlayerProjectionTrading>;
    projectionBoardTime?: string;
    projectionStartTime?: string;
    updateProjection: ({
      change,
      initialProjection
    }: {
      change: GamePlayerProjectionTrading;
      initialProjection: GamePlayerProjectionTrading;
    }) => void;
  }) => void;
  simulationGeneratedProjections: Array<GamePlayerProjectionTrading>;
  market: {
    league_id: string;
    market: string;
    market_id: string;
    stat_type_id: number;
  };
  statTypes: Array<LeagueProjectionAttributes>;
  updateProjection: ({
    change,
    initialProjection
  }: {
    change: GamePlayerProjectionTrading;
    initialProjection: GamePlayerProjectionTrading;
  }) => void;
}

export const ProjectionRows = ({
  onBulkTimeChange,
  marketExistingProjections,
  newAndChangedProjections,
  simulationGeneratedProjections,
  market,
  statTypes,
  updateProjection
}: ProjectionRowsProps) => {
  const [showProjections, setShowProjections] = useState(true);

  const [simProjections, setSimProjections] = useState<
    Array<GamePlayerProjectionTrading>
  >(
    simulationGeneratedProjections.sort((a, b) => (a.rank || 0) - (b.rank || 0))
  );

  const statTypeName = statTypes.find(
    (statType) =>
      statType.leagueProjectionFilterId === String(market.stat_type_id)
  )?.leagueProjectionFilterName;

  if (
    !market.stat_type_id ||
    !statTypeName ||
    simulationGeneratedProjections.length === 0
  ) {
    return undefined;
  }

  return (
    <div className="border-soFresh-140 inline-flex w-full flex-col items-start justify-start rounded border">
      <StatTypeHeader
        newAndChangedProjections={newAndChangedProjections}
        onBulkTimeChange={onBulkTimeChange}
        onToggleShowProjections={() => setShowProjections(!showProjections)}
        setSimProjections={setSimProjections}
        showProjections={showProjections}
        simProjections={simProjections}
        statTypeName={statTypeName}
        updateProjection={updateProjection}
      />
      <div
        className={classNames(
          'grid max-h-[33rem] w-full grid-cols-[max-content_max-content_max-content_max-content_max-content_1fr] place-items-center gap-4 overflow-auto py-4',
          showProjections ? 'inherit' : 'hidden'
        )}
      >
        <ProjectionsHeader
          newAndChangedProjections={newAndChangedProjections}
          simProjections={simProjections}
          statTypeName={statTypeName}
          updateProjection={updateProjection}
        />

        {simProjections.map((initialProjection) => {
          if (!initialProjection) {
            return undefined;
          }

          const marketExistingProjection = marketExistingProjections?.find(
            (existingProjection) =>
              existingProjection.foreignId === initialProjection.foreignId &&
              existingProjection.statTypeId === initialProjection.statTypeId &&
              existingProjection.isPromo === false
          );

          const projectionChanges = findProjectionByProjectionId({
            projectionArray: newAndChangedProjections,
            projectionId: initialProjection.projectionId
          });

          return (
            <ProjectionRow
              existingProjection={marketExistingProjection}
              onChange={updateProjection}
              projectionChanges={projectionChanges}
              key={initialProjection.projectionId}
              initialProjection={initialProjection}
            />
          );
        })}
      </div>
    </div>
  );
};
